import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Mic, MicOff, PhoneOff, Bot, Loader2, Play, ChevronLeft,
  Sparkles, AlertTriangle, CheckCircle2, RotateCcw, Send, Video,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { getApiUrlsWithFallback } from "@/config/api";

// Brand ink for the dark Riya panel (matches the landing dark bands).
const INK = "hsl(164 33% 7%)";
// Hard safety cap on the paid realtime session. Consultative calls (discover →
// position → book) legitimately run a few minutes, so allow up to 5 minutes.
const MAX_SECONDS = 300;

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.in", "hotmail.com", "outlook.com",
  "live.com", "icloud.com", "aol.com", "proton.me", "protonmail.com", "gmx.com",
  "mail.com", "yandex.com", "zoho.com", "rediffmail.com",
]);
const isValidEmailFormat = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const validateWorkEmail = (e) => {
  if (!isValidEmailFormat(e)) return "Please enter a valid email address.";
  if (FREE_EMAIL_DOMAINS.has(e.split("@")[1].toLowerCase()))
    return "Please use your work email, not a personal address.";
  return "";
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const CAP_MESSAGES = {
  email_cap: "You've reached today's limit of live demos for this email. Please try again tomorrow.",
  ip_cap: "This network has reached today's demo limit. Please try again later.",
  email_required: "A valid work email is required to start.",
  server_misconfigured: "The live demo isn't available right now. Please try again later.",
  mint_failed: "We couldn't start Riya right now. Please try again in a moment.",
  network_error: "Network issue starting the session. Please check your connection and retry.",
};

export default function ZiaVoiceDemo({ onBack }) {
  // Personalised-link params (e.g. a LinkedIn outreach link:
  // /hirewithus/demo?name=Rahul&company=Microsoft&role=Head+of+TA)
  const [params] = useSearchParams();
  const paramCompany = (params.get("company") || "").trim();
  const paramRole = (params.get("role") || "").trim();

  const [phase, setPhase] = useState("gate");          // gate | starting | live | ended
  const [connected, setConnected] = useState(false);
  const [ziaSpeaking, setZiaSpeaking] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [currentZia, setCurrentZia] = useState("");
  const [, setTick] = useState(0);
  const [linkStatus, setLinkStatus] = useState(null);  // null | sending | sent | booked | failed
  const [meetLink, setMeetLink] = useState("");
  const [endRequested, setEndRequested] = useState(false); // Riya called end_call

  // Lead gate (name prefilled from a personalised link when present)
  const [name, setName] = useState(params.get("name") || "");
  const [email, setEmail] = useState("");
  const [gateError, setGateError] = useState("");

  const streamRef = useRef(null);
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);
  const convRef = useRef([]);          // [{ role, text }]
  const nameRef = useRef("");
  const emailRef = useRef("");
  const endingRef = useRef(false);
  const startedRef = useRef(false);
  const timerRef = useRef(null);
  const fnCallsRef = useRef({});       // call_id -> { name, args }
  const ziaSpokeRef = useRef(false);   // Riya has actually produced speech at least once
  const liveStartRef = useRef(0);      // ms timestamp when the call went live
  const lastActivityRef = useRef(0);   // ms of the last transcript activity (reliable "speaking" signal)
  // Auto-end is honored ONLY after Riya has spoken AND the call has run a real
  // minimum — so a stray/early end_call can never hang up before a conversation.
  const canAutoEnd = () => ziaSpokeRef.current && liveStartRef.current > 0 &&
    (Date.now() - liveStartRef.current) > 12000;

  const push = (role, text) => {
    const t = (text || "").trim();
    if (!t) return;
    const last = convRef.current[convRef.current.length - 1];
    if (last && last.role === role && last.text === t) return;
    convRef.current = [...convRef.current, { role, text: t }];
    setTick((n) => n + 1);
  };

  const stopEverything = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { dcRef.current && dcRef.current.close(); } catch {}
    try {
      if (pcRef.current) { pcRef.current.getSenders().forEach((s) => s.track?.stop()); pcRef.current.close(); }
    } catch {}
    try {
      if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current.srcObject = null; audioElRef.current.remove(); }
    } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    pcRef.current = null; dcRef.current = null; audioElRef.current = null;
  }, []);

  useEffect(() => () => stopEverything(), [stopEverything]);

  const endCall = useCallback(() => {
    if (endingRef.current) return;
    endingRef.current = true;
    setConnected(false);
    setZiaSpeaking(false);
    stopEverything();
    setPhase("ended");
  }, [stopEverything]);

  const endRef = useRef(endCall);
  useEffect(() => { endRef.current = endCall; }, [endCall]);

  // Auto-end after Riya's goodbye: once she calls end_call, wait until she stops
  // speaking, then close after a short grace so the last words play out fully.
  // (If she starts speaking again the timer is cleared and we keep waiting.)
  useEffect(() => {
    if (!endRequested || phase !== "live" || ziaSpeaking) return;
    const t = setTimeout(() => { if (!endingRef.current) endRef.current(); }, 1400);
    return () => clearTimeout(t);
  }, [endRequested, ziaSpeaking, phase]);

  // Hard fallback so a stuck audio state can never leave the call hanging open.
  useEffect(() => {
    if (!endRequested) return;
    const t = setTimeout(() => { if (!endingRef.current) endRef.current(); }, 12000);
    return () => clearTimeout(t);
  }, [endRequested]);

  // Deterministic completion: once the booking is done (link sent / slot booked),
  // end the call after Riya has been QUIET for a few seconds — measured from the
  // TRANSCRIPT stream, NOT the audio element (whose 'pause'/'ended' never fire on a
  // live WebRTC stream, which is exactly why the call used to hang open). If she
  // speaks again (answering a follow-up), lastActivity resets and we keep waiting.
  useEffect(() => {
    const bookingDone = linkStatus === "booked" || linkStatus === "sent";
    if (!bookingDone || phase !== "live") return;
    const iv = setInterval(() => {
      if (!endingRef.current && lastActivityRef.current &&
          Date.now() - lastActivityRef.current > 4500) {
        clearInterval(iv);
        endRef.current();
      }
    }, 700);
    return () => clearInterval(iv);
  }, [linkStatus, phase]);

  // Keep the Speaking / Listening indicator honest — derive it from transcript
  // activity, since the audio element's play/pause events don't fire reliably on a
  // continuous WebRTC stream (that's why it was stuck on "Speaking…").
  useEffect(() => {
    if (phase !== "live") return;
    const iv = setInterval(() => {
      setZiaSpeaking(lastActivityRef.current > 0 && Date.now() - lastActivityRef.current < 1500);
    }, 400);
    return () => clearInterval(iv);
  }, [phase]);

  // Elapsed timer + hard cap.
  useEffect(() => {
    if (phase !== "live") return;
    timerRef.current = setInterval(() => {
      setElapsed((t) => {
        if (t + 1 >= MAX_SECONDS) { if (!endingRef.current) endRef.current(); return MAX_SECONDS; }
        return t + 1;
      });
    }, 1000);
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [phase]);

  // Riya's send_booking_link tool → email the link, then feed the result back.
  const handleSendLink = useCallback(async (callId, args) => {
    setLinkStatus("sending");
    const payload = {
      name: nameRef.current,
      email: emailRef.current,
      phone: (args?.phone || "").toString(),
      intent: (args?.intent || "unknown").toString(),
      role_count: (args?.role_count || "").toString(),
      notes: (args?.notes || "").toString(),
      preferred_day: (args?.preferred_day || "").toString(),
      preferred_time: (args?.preferred_time || "").toString(),
      preferred_datetime: (args?.preferred_datetime || "").toString(),
    };
    let ok = false, result = null;
    for (const url of getApiUrlsWithFallback("/api/demo/zia/send-link")) {
      try {
        const res = await fetch(url, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        const j = await res.json().catch(() => null);
        if (res.ok && j && j.ok) { ok = true; result = j; break; }
        if (j && !j.ok) break; // definitive failure (e.g. no_email) — don't retry
      } catch (e) { /* try next base */ }
    }
    if (ok && result?.meet_link) setMeetLink(result.meet_link);
    setLinkStatus(ok ? (result?.booked ? "booked" : "sent") : "failed");
    // Tell the model what happened so Riya can respond truthfully.
    try {
      const dc = dcRef.current;
      if (dc && dc.readyState === "open") {
        dc.send(JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify(
              ok
                ? (result?.booked
                    ? { ok: true, booked: true, note: "Meeting booked — Google Meet invite emailed to them." }
                    : { ok: true, booked: false, note: "Self-serve booking link emailed to them." })
                : { ok: false, note: "Could not send just now; the team will still follow up." }
            ),
          },
        }));
        dc.send(JSON.stringify({ type: "response.create" }));
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  const onDcMessage = useCallback((ev) => {
    let data = null;
    try { data = JSON.parse(ev.data); } catch { return; }
    const type = (data?.type || "").toString();

    // Riya (assistant) transcript streaming
    if (/response\.(output_audio_transcript|output_text|audio_transcript)\.delta$/.test(type)) {
      const d = data?.delta || data?.text || "";
      lastActivityRef.current = Date.now();
      if (d) { setCurrentZia((p) => p + d); ziaSpokeRef.current = true; }
      return;
    }
    if (/response\.(output_audio_transcript|output_text|audio_transcript)\.done$/.test(type)) {
      const finalText = (data?.transcript || data?.text || "").trim();
      lastActivityRef.current = Date.now();
      if (finalText) push("assistant", finalText);
      setCurrentZia("");
      return;
    }
    // Visitor transcript
    if (type === "conversation.item.input_audio_transcription.completed") {
      lastActivityRef.current = Date.now();
      push("user", (data?.transcript || "").trim());
      return;
    }
    // ── Function calls (send_booking_link) ──────────────────────────────
    // Capture the call's name + id when the item appears...
    if (type === "response.output_item.added" || type === "response.output_item.done") {
      const item = data?.item;
      if (item && item.type === "function_call" && item.call_id) {
        fnCallsRef.current[item.call_id] = {
          name: item.name || fnCallsRef.current[item.call_id]?.name,
          args: item.arguments || fnCallsRef.current[item.call_id]?.args || "",
        };
        // Fallback: end_call (no args) may only surface here — still gated.
        if (item.name === "end_call" && type === "response.output_item.done" && canAutoEnd())
          setEndRequested(true);
      }
      return;
    }
    if (type === "response.function_call_arguments.delta" && data?.call_id) {
      const rec = fnCallsRef.current[data.call_id] || { name: data?.name, args: "" };
      rec.args = (rec.args || "") + (data?.delta || "");
      fnCallsRef.current[data.call_id] = rec;
      return;
    }
    if (type === "response.function_call_arguments.done" && data?.call_id) {
      const rec = fnCallsRef.current[data.call_id] || {};
      const name = rec.name || data?.name;
      const rawArgs = data?.arguments || rec.args || "{}";
      let args = {};
      try { args = JSON.parse(rawArgs); } catch { args = {}; }
      if (name === "send_booking_link") handleSendLink(data.call_id, args);
      // Only honor end_call after Riya has spoken AND the call has run a real
      // minimum — a premature end_call must never hang up before a conversation.
      else if (name === "end_call" && canAutoEnd()) setEndRequested(true);
      return;
    }
  }, [handleSendLink]);

  const startRealtime = useCallback(async () => {
    const payload = {
      name: nameRef.current, email: emailRef.current,
      company: paramCompany, role: paramRole,
    };
    let json = null, capReason = null;
    for (const url of getApiUrlsWithFallback("/api/demo/zia/session")) {
      try {
        const res = await fetch(url, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => null);
        if (res.ok && body && body.ok) { json = body; break; }
        if (body && body.reason) capReason = body.reason;
      } catch (e) { /* try next */ }
    }
    if (!json) {
      setError(CAP_MESSAGES[capReason] || "We couldn't start Riya right now. Please try again.");
      stopEverything(); endingRef.current = false; setPhase("gate");
      return;
    }
    const KEY = json?.session?.client_secret?.value ?? json?.session?.client_secret ?? json?.client_secret?.value ?? null;
    if (!KEY) { setError("We couldn't start the session (no key). Please try again."); setPhase("gate"); return; }

    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      const on = () => { setZiaSpeaking(true); ziaSpokeRef.current = true; };
      const off = () => setZiaSpeaking(false);
      audioEl.addEventListener("playing", on);
      audioEl.addEventListener("play", on);
      audioEl.addEventListener("pause", off);
      audioEl.addEventListener("ended", off);
      pc.ontrack = (e) => { if (e.streams && e.streams[0]) audioEl.srcObject = e.streams[0]; };

      const micTrack = streamRef.current?.getAudioTracks?.()[0];
      if (micTrack) pc.addTrack(micTrack, streamRef.current);

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onopen = () => {
        setConnected(true); setPhase("live");
        liveStartRef.current = Date.now();
        try { dc.send(JSON.stringify({ type: "response.create" })); } catch (e) {}
      };
      dc.onmessage = onDcMessage;

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdpResp = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: `Bearer ${KEY}`, "Content-Type": "application/sdp" },
        body: offer.sdp,
      });
      if (!sdpResp.ok) {
        setError("The connection to Riya failed. Please try again.");
        stopEverything(); endingRef.current = false; setPhase("gate");
        return;
      }
      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch (err) {
      setError("Something went wrong starting the call. Please try again.");
      stopEverything(); endingRef.current = false; setPhase("gate");
    }
  }, [onDcMessage, stopEverything, paramCompany, paramRole]);

  const begin = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setError(""); setPhase("starting"); setElapsed(0); setLinkStatus(null); setEndRequested(false);
    convRef.current = []; fnCallsRef.current = {}; endingRef.current = false; ziaSpokeRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      await startRealtime();
    } catch (err) {
      setError("We couldn't access your microphone. Please allow access and try again.");
      setPhase("gate");
    } finally {
      startedRef.current = false;
    }
  };

  const submitGate = () => {
    const n = name.trim();
    if (!n) { setGateError("Please enter your name."); return; }
    const emailErr = validateWorkEmail(email.trim());
    if (emailErr) { setGateError(emailErr); return; }
    setGateError("");
    nameRef.current = n;
    emailRef.current = email.trim().toLowerCase();
    // Warm the company research NOW (fire-and-forget) so it's cached by the time
    // the call connects — it overlaps mic permission instead of blocking the mint.
    try {
      const url = getApiUrlsWithFallback("/api/demo/zia/prewarm")[0];
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" },
                   body: JSON.stringify({ email: emailRef.current }) }).catch(() => {});
    } catch (e) { /* non-fatal */ }
    begin();
  };

  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks?.()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };

  const restart = () => {
    stopEverything();
    endingRef.current = false; convRef.current = []; fnCallsRef.current = {}; ziaSpokeRef.current = false;
    setConnected(false); setZiaSpeaking(false); setCurrentZia("");
    setError(""); setElapsed(0); setLinkStatus(null); setEndRequested(false); setPhase("gate");
  };

  // ═══════════════════════════ GATE / START ═══════════════════════════════
  if (phase === "gate" || phase === "starting") {
    return (
      <div className="max-w-3xl mx-auto py-8">
        {onBack && (
          <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-6">
            <ChevronLeft size={16} /> Back
          </button>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg ring-2 ring-border">
              <Bot size={30} className="text-primary-foreground" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-1">
              <Sparkles size={14} /> AI HIRING CONSULTANT
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
              Meet Riya, Your AI Hiring Consultant
            </h1>
            <div className="text-sm text-muted-foreground">Discover how AI can transform your hiring — in just a few minutes.</div>
          </div>
        </div>

        <p className="text-muted-foreground mb-6 text-base leading-relaxed">
          Riya would like to understand your hiring pain points and explain how SearchJob could help resolve them —
          in just a few minutes. Ask questions, explore real use cases, and see how
          <span className="text-foreground font-medium"> SearchJob.ai</span> could fit into your hiring process.
        </p>

        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive p-3 text-sm mb-6">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card p-6 max-w-md">
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Your name</label>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Nair"
            className="w-full rounded-xl bg-secondary/40 border border-border px-4 py-3 text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 mb-4"
          />
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Work email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitGate(); }} placeholder="you@company.com"
            className="w-full rounded-xl bg-secondary/40 border border-border px-4 py-3 text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
          {gateError && (
            <div className="flex items-start gap-2 text-sm text-destructive mt-3">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" /> <span>{gateError}</span>
            </div>
          )}
          <button
            onClick={submitGate} disabled={phase === "starting"}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold shadow-lg hover:bg-primary/90 transition disabled:opacity-60"
          >
            {phase === "starting"
              ? (<><Loader2 size={18} className="animate-spin" /> Connecting to Riya…</>)
              : (<><Play size={18} /> Talk to Riya</>)}
          </button>
          <p className="text-[11px] text-muted-foreground mt-3">
            Your microphone audio is streamed to Riya to run the live conversation. We'll email your booking link to the address above.
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════ ENDED ══════════════════════════════════════
  if (phase === "ended") {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 size={34} className="text-success" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">That's Riya.</h1>
        <p className="text-muted-foreground mb-2">
          That's exactly the experience your candidates get on their first screening call — in their own language, in minutes.
        </p>
        {linkStatus === "booked" && (
          <div className="mb-6">
            <p className="text-sm text-success font-medium mb-3">✓ Your demo is booked — the Google Calendar invite is in your inbox.</p>
            {meetLink && (
              <a href={meetLink} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition">
                <Video size={16} /> Join on Google Meet
              </a>
            )}
          </div>
        )}
        {linkStatus === "sent" && (
          <p className="text-sm text-success font-medium mb-6">✓ We've emailed your booking link. Pick a time that suits you!</p>
        )}
        {(linkStatus === null || linkStatus === "failed") && (
          <p className="text-sm text-muted-foreground mb-6">Our team will follow up shortly.</p>
        )}
        <div className="flex items-center justify-center gap-3">
          {onBack && (
            <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent transition">
              <ChevronLeft size={16} /> Back to demos
            </button>
          )}
          <button onClick={restart} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition">
            <RotateCcw size={16} /> Talk again
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════ LIVE CALL ══════════════════════════════════
  const messages = convRef.current;
  return (
    <div className="max-w-4xl mx-auto py-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
          <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Bot size={16} /></span>
          <span>Riya <span className="text-muted-foreground">· SearchJob AI</span></span>
          <span className="ml-1 flex items-center gap-1.5 text-xs">
            <span className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-amber-500 animate-pulse"}`} />
            <span className="text-muted-foreground">{connected ? "Connected" : "Connecting…"}</span>
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums text-foreground">
          {fmt(elapsed)}
        </span>
      </div>

      {/* Riya panel */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg flex flex-col items-center justify-center p-10 mb-5" style={{ backgroundColor: INK }}>
        <div className={`relative mb-4 ${ziaSpeaking ? "scale-105" : ""} transition-transform`}>
          <div className={`absolute inset-0 rounded-full bg-primary/40 ${ziaSpeaking ? "animate-ping" : ""}`} />
          <div className="relative w-24 h-24 rounded-full bg-primary flex items-center justify-center shadow-lg">
            <Bot size={46} className="text-primary-foreground" />
          </div>
        </div>
        <div className="text-white/60 text-xs font-semibold tracking-wide mb-1">RIYA · AI VOICE RECRUITER</div>
        <div className="text-white text-sm font-medium">
          {ziaSpeaking ? "Speaking…" : connected ? "Listening — just talk naturally" : "Connecting…"}
        </div>
        {linkStatus && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
            {linkStatus === "sending" && (<><Loader2 size={13} className="animate-spin" /> Booking your walkthrough…</>)}
            {linkStatus === "booked" && (<><CheckCircle2 size={13} className="text-primary-foreground" /> Meeting booked — invite sent to your email ✓</>)}
            {linkStatus === "sent" && (<><Send size={13} className="text-primary-foreground" /> Booking link sent to your email ✓</>)}
            {linkStatus === "failed" && (<><AlertTriangle size={13} /> We'll follow up to confirm your slot</>)}
          </div>
        )}
      </div>

      {/* Live transcript */}
      <div className="rounded-2xl border border-border bg-card p-5 max-h-56 overflow-y-auto mb-5">
        <div className="text-xs font-semibold text-muted-foreground mb-3">LIVE TRANSCRIPT</div>
        {messages.length === 0 && !currentZia && (
          <p className="text-sm text-muted-foreground italic">Riya will say hello and begin shortly…</p>
        )}
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "assistant" ? "" : "text-right"}>
              <div className="text-[11px] text-muted-foreground mb-0.5">{m.role === "assistant" ? "Riya" : "You"}</div>
              <div className={`inline-block px-3 py-2 rounded-lg text-sm leading-relaxed max-w-[90%] ${
                m.role === "assistant" ? "bg-secondary/60 text-foreground" : "bg-primary text-primary-foreground"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {currentZia && (
            <div>
              <div className="text-[11px] text-muted-foreground mb-0.5">Riya</div>
              <div className="inline-block px-3 py-2 rounded-lg text-sm leading-relaxed max-w-[90%] bg-secondary/60 text-foreground border border-primary/30">
                {currentZia}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={toggleMic}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition"
        >
          {micOn ? <Mic size={16} /> : <MicOff size={16} className="text-destructive" />}
          {micOn ? "Mute" : "Unmute"}
        </button>
        <button
          onClick={endCall}
          className="inline-flex items-center gap-2 rounded-full border border-destructive/40 text-destructive px-4 py-2.5 text-sm font-medium hover:bg-destructive/10 transition"
        >
          <PhoneOff size={16} /> End call
        </button>
      </div>
    </div>
  );
}
